import {Card, Button, FormControl, FormField} from 'platform/components';
import {Box, Hide, VStack, Grid} from 'platform/foundation';

import {useFieldArray, useWatch} from 'react-hook-form';
import {useSelector} from 'react-redux';

import {selectTenant} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {selectCashRegisterLimitsDocumentTypes, selectCashRegisterLimitsTypes} from '@dms/teas';

import {CurrencyCodeType} from 'shared/web';

import {DefinitionFormType} from './types';
import {emptyRestrictionItem} from './utils';

interface RestrictionListProps {
  control: FormControl<DefinitionFormType>;
}

export function RestrictionList(props: RestrictionListProps) {
  const {data: {currency} = {}} = useSelector(selectTenant);
  const types = useSelector(selectCashRegisterLimitsTypes);
  const documentTypes = useSelector(selectCashRegisterLimitsDocumentTypes);

  const {append, remove, fields} = useFieldArray({
    control: props.control,
    name: 'restrictions',
  });

  const system = useWatch({
    control: props.control,
    name: 'system',
  });

  const currencyFromForm = useWatch({
    control: props.control,
    name: 'currency',
  });

  const handleCreateNewItem = () => {
    append(emptyRestrictionItem(currency as CurrencyCodeType));
  };

  const handleRemoveItem = (index: number) => () => {
    remove(index);
  };

  const documentTypeOptions = documentTypes.map((documentType) => ({
    value: documentType.id,
    label: documentType.label,
  }));
  const typeOptions = types.map((type) => ({
    value: type.id,
    label: type.label,
  }));

  return (
    <VStack spacing={4}>
      {fields.map((field, index) => {
        const disableRemove = system || fields.length === 1;
        const partlyChangeable = index === 0 ? system : false;

        return (
          <Card variant="inlineGrey" key={field.id}>
            <Box>
              <Grid columns={4}>
                <FormField
                  type="text"
                  control={props.control}
                  name={`restrictions.${index}.amount.amount`}
                  label={i18n.t('entity.cashRegisterLimits.labels.amount')}
                  suffix={currencyFromForm}
                  data-testid={testIds.settings.cashLimits('amount')}
                />
                <FormField
                  type="multiChoice"
                  control={props.control}
                  name={`restrictions.${index}.documentTypes`}
                  label={i18n.t('entity.cashRegisterLimits.labels.documentType')}
                  data-testid={testIds.settings.cashLimits('documentType')}
                  options={documentTypeOptions}
                  isNotClearable
                />
                <FormField
                  type="date"
                  control={props.control}
                  name={`restrictions.${index}.validFrom`}
                  label={i18n.t('entity.accounting.labels.validFrom')}
                  data-testid={testIds.settings.cashLimits('validFrom')}
                  isDisabled={partlyChangeable}
                />
                <FormField
                  type="choice"
                  control={props.control}
                  name={`restrictions.${index}.type`}
                  label={i18n.t('entity.cashRegisterLimits.labels.restriction')}
                  data-testid={testIds.settings.cashLimits('restriction')}
                  options={typeOptions}
                  tooltip={i18n.t('entity.cashRegisterLimits.labels.restrictionTooltip')}
                  isNotClearable
                />
              </Grid>
              {!disableRemove && (
                <Box paddingTop={3}>
                  <Button
                    leftIcon="content/remove_circle"
                    variant="errorLink"
                    size="small"
                    title={i18n.t('entity.accounting.actions.removeValidity')}
                    onClick={handleRemoveItem(index)}
                    data-testid={testIds.settings.cashLimits(`remove-${index}`)}
                  />
                </Box>
              )}
            </Box>
          </Card>
        );
      })}
      <Hide when={system}>
        <Box>
          <Button
            variant="link"
            leftIcon="content/add_circle"
            title={i18n.t('entity.accounting.actions.addValidity')}
            onClick={handleCreateNewItem}
            data-testid={testIds.settings.cashLimits('addValidity')}
          />
        </Box>
      </Hide>
    </VStack>
  );
}
